import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import ExerciseCategoryTabs from '../components/ExerciseCategoryTabs';
import PickerModal, {
  ListItem,
  PickerModalNoData,
  PickerModalSearch,
} from '../../../shared/_TBComponents/PickerModal';
import Spinner from '../../../shared/_TBComponents/Spinner';

import { ColorPrimary, ColorWhite } from '../../../shared/GlobalStyles';
import { journalTracker } from '../../../shared/utils/amplitudeHelper';
import { setTempExercise } from '../ducks/journalActions';

const ExercisesList = styled('div')`
  background-color: ${ColorWhite};
  color: ${ColorPrimary};
  margin: 0 auto;
  text-align: center;
  width: 90%;
`;

const ExercisesContainer = styled('div')`
  height: 400px;
  margin: 8px auto 0 auto;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  width: 100%;
`;

const ExerciseItem = (props) => (
  <ListItem justifyContent='space-between' {...props} />
);

const SelectExerciseModal = ({ onRequestClose }) => {
  const exercises = useSelector((state) => state.journal.data.exercises);
  const isExercisesLoading = useSelector(
    (state) => state.journal.ui.isExercisesLoading,
  );
  const exercisesError = useSelector(
    (state) => state.journal.data.exercisesError,
  );
  const [searchString, setSearchString] = useState('');
  const dispatch = useDispatch();

  const filteredExercises = useMemo(
    () => (exercises && Array.isArray(exercises)
      ? exercises.filter(
        (exercise) => exercise.name.toLowerCase().includes(searchString.toLowerCase()),
      )
      : []),
    [exercises, searchString],
  );

  const exercisesAvailable = !!filteredExercises.length && !exercisesError;

  const handleSelectExercise = (exercise) => {
    dispatch(setTempExercise(exercise));
    onRequestClose();
    journalTracker('Select exercise modal', 'Filter options - Exercise selected');
  };

  const renderNoData = () => {
    let message;
    if (exercisesError) {
      message = exercisesError?.message || exercisesError?.response?.data?.message;
    } else if (searchString.length) {
      message = 'No exercises found matching your search.';
    } else {
      message = 'No exercises found.';
    }

    return <PickerModalNoData message={message} />;
  };

  const renderExercisesList = () => {
    if (isExercisesLoading) {
      return <Spinner large aria-label='Loading exercises...' />;
    }

    if (!exercisesAvailable) {
      return renderNoData();
    }

    return (
      <ExercisesList>
        {filteredExercises.map((exercise) => (
          <ExerciseItem key={exercise.id} onClick={() => handleSelectExercise(exercise)} aria-label={`Select exercise: ${exercise.name}`}>
            <p>{exercise.name}</p>
          </ExerciseItem>
        ))}
      </ExercisesList>
    );
  };

  return (
    <PickerModal
      onRequestClose={onRequestClose}
      headerText='Select an Exercise'
      headerIcon='lift'
    >
      <ExerciseCategoryTabs />
      <PickerModalSearch
        placeholder='Search Exercise'
        ariaLabel='Search exercises by name'
        value={searchString}
        onChange={(e) => setSearchString(e.target.value)}
      />
      <ExercisesContainer>
        {renderExercisesList()}
      </ExercisesContainer>
    </PickerModal>
  );
};

SelectExerciseModal.propTypes = {
  onRequestClose: PropTypes.func.isRequired,
};

export default SelectExerciseModal;
